import { useCallback } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { ButtonGroup, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { TransformMode } from '@fabric-web/3d-viewer';
import { OutlinedIconButton } from './styles';
import { CuboidBase, CuboidDataHandlers, CuboidState } from '../../types';

type CuboidSaveButtonGroupProps<CuboidT extends CuboidBase> = {
  cuboidState: CuboidState<CuboidT>;
  dataHandlers: CuboidDataHandlers<CuboidT>;
};

export const CuboidSaveButtonGroup = <CuboidT extends CuboidBase>({
  cuboidState,
  dataHandlers,
}: CuboidSaveButtonGroupProps<CuboidT>) => {
  const [currentCuboid, setCurrentCuboid] = useAtom(cuboidState.currentCuboid);
  const setTransformMode = useSetAtom(cuboidState.cuboidTransformMode);
  const setIsCuboidEditorEnabled = useSetAtom(cuboidState.isCuboidEditorEnabled);

  const handleCancelCuboid = useCallback(() => {
    setCurrentCuboid(undefined);
    setIsCuboidEditorEnabled(false);
  }, [setCurrentCuboid, setIsCuboidEditorEnabled]);

  const handleSaveCuboid = useCallback(() => {
    setTransformMode(TransformMode.View);
    setIsCuboidEditorEnabled(false);
  }, [setTransformMode, setIsCuboidEditorEnabled]);

  const handleRemoveCuboid = useCallback(() => {
    if (currentCuboid) {
      dataHandlers.deleteCuboid(currentCuboid.cuboidData);
    }
    setCurrentCuboid(undefined);
    setIsCuboidEditorEnabled(false);
  }, [currentCuboid, dataHandlers, setCurrentCuboid, setIsCuboidEditorEnabled]);

  return (
    <ButtonGroup sx={{ paddingBottom: '10px' }}>
      <Tooltip title="Save">
        <OutlinedIconButton onClick={handleSaveCuboid} disabled={!currentCuboid}>
          <SaveIcon style={{ color: '#fff' }} />
        </OutlinedIconButton>
      </Tooltip>

      <Tooltip title="Remove" sx={{ marginLeft: '20px' }}>
        <OutlinedIconButton onClick={handleRemoveCuboid} disabled={!currentCuboid}>
          <DeleteOutlineIcon style={{ color: '#fff' }} />
        </OutlinedIconButton>
      </Tooltip>

      <Tooltip title="Cancel" sx={{ marginLeft: '20px' }}>
        <OutlinedIconButton onClick={handleCancelCuboid} disabled={!currentCuboid}>
          <CloseIcon style={{ color: '#fff' }} />
        </OutlinedIconButton>
      </Tooltip>
    </ButtonGroup>
  );
};
